import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';

/**
 * Click-to-open info tooltip/popover anchored to an "i" icon.
 * Implemented with a portal so it doesn't get clipped by parent containers.
 */
export function ClickInfoTooltip({
  label,
  title,
  children,
  className = '',
  tooltipClassName = '',
}) {
  const anchorRef = useRef(null);
  const tooltipRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setCoords({
      top: rect.bottom + 8,
      left: rect.left + rect.width / 2,
    });
  }, []);

  const show = useCallback(() => {
    updatePosition();
    setVisible(true);
  }, [updatePosition]);

  const hide = useCallback(() => setVisible(false), []);

  const toggle = useCallback(() => {
    setVisible((v) => {
      const next = !v;
      if (next) updatePosition();
      return next;
    });
  }, [updatePosition]);

  useEffect(() => {
    if (!visible) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') hide();
    };

    const onMouseDown = (e) => {
      const a = anchorRef.current;
      const t = tooltipRef.current;
      const target = e.target;
      if (a && a.contains(target)) return;
      if (t && t.contains(target)) return;
      hide();
    };

    const onScrollOrResize = () => updatePosition();
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);

    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [visible, hide, updatePosition]);

  return (
    <span className={`inline-flex items-start ${className}`}>
      <button
        ref={anchorRef}
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggle();
        }}
        aria-label={label || title || 'More information'}
        aria-expanded={visible}
        className="inline-flex shrink-0 p-0.5 rounded text-gray-400 dark:text-gray-500 hover:text-[#03396c] dark:hover:text-blue-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-[#03396c]"
      >
        <Info className="h-3.5 w-3.5" aria-hidden />
      </button>

      {visible &&
        createPortal(
          <div
            ref={tooltipRef}
            role="tooltip"
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              transform: 'translateX(-50%)',
              zIndex: 9999,
            }}
            className={`pointer-events-auto w-max max-w-[320px] rounded-md px-3 py-2.5 text-[11px] font-normal normal-case tracking-normal leading-snug text-white bg-gray-900 dark:bg-gray-700 shadow-lg ${tooltipClassName}`}
          >
            {title ? (
              <div className="text-[11px] font-semibold mb-1 text-white/95">{title}</div>
            ) : null}
            <div className="text-[11px] text-white/90">{children}</div>
          </div>,
          document.body,
        )}
    </span>
  );
}

export default ClickInfoTooltip;

